import React, { useState, useEffect } from 'react';
import Checkbox from '../checkbox/checkbox';
import InputText from '../inputText/inputText';
import styles from './rateStrucher.module.scss';

interface RateStructureTypeItem {
    rate_structure_type_id: number;
    rate_structure_type_name: string;
}

interface MealPlanTypeItem {
    meal_plan_type_id: number;
    meal_plan_type_name: string;
}

interface RateStrucherProps {
    value?: number | null;
    onChange?: (value: number) => void;
    rateStructureTypes?: RateStructureTypeItem[];
    mealPlanTypes?: MealPlanTypeItem[];
    selectedMealPlans?: number[];
    onMealPlanChange?: (mealPlanIds: number[]) => void;
    gitFitMinRoomCount?: number;
    gitFitMinPersonCount?: number;
    onGitFitChange?: (rooms: number, persons: number) => void;
    showValidationErrors?: boolean;
    mealPlanError?: string;
    gitFitError?: string;
}

const RateStrucher: React.FC<RateStrucherProps> = ({ 
    value, 
    onChange,
    rateStructureTypes = [],
    mealPlanTypes = [],
    selectedMealPlans: selectedMealPlansFromParent = [],
    onMealPlanChange,
    gitFitMinRoomCount = 10,
    gitFitMinPersonCount = 15,
    onGitFitChange,
    showValidationErrors = false,
    mealPlanError,
    gitFitError
}) => {
    // Get the first rate structure type ID as default if no value provided
    const defaultValue = rateStructureTypes.length > 0 ? rateStructureTypes[0].rate_structure_type_id : null;
    const [selectedValue, setSelectedValue] = useState<number | null>(value !== undefined ? value : defaultValue);
    const [selectedMealPlanIds, setSelectedMealPlanIds] = useState<number[]>(selectedMealPlansFromParent);

    // Update selectedValue when value prop changes
    useEffect(() => {
        if (value !== undefined) {
            setSelectedValue(value);
        }
    }, [value]);

    // Update local selectedMealPlanIds when parent prop changes
    useEffect(() => {
        setSelectedMealPlanIds(selectedMealPlansFromParent);
    }, [selectedMealPlansFromParent]);

    const handleChange = (newValue: number) => {
        console.log(newValue,selectedValue);
        if(selectedValue === 2 && newValue !== 2){
            setSelectedMealPlanIds([]);
        }
        setSelectedValue(newValue);
        onChange?.(newValue);
    };

    const handleMealPlanToggle = (mealPlanId: number, checked: boolean) => {
        const newSelectedIds = checked
            ? [...selectedMealPlanIds, mealPlanId]
            : selectedMealPlanIds.filter(id => id !== mealPlanId);
        
        setSelectedMealPlanIds(newSelectedIds);
        onMealPlanChange?.(newSelectedIds);
    };

    // Get the selected rate structure name for conditional rendering
    const getSelectedRateStructureName = (): number => {
        const selected = rateStructureTypes.find(item => item.rate_structure_type_id === selectedValue);
        return selected?.rate_structure_type_id || 0;
    };

    return (
        <div className={styles.rateStructureContainer}>
            <div className={styles.rateStructureHeader}>
                <b>Rate Structure:</b>
                <div className={styles.radioGroup}>
                    {rateStructureTypes.map((option) => (
                        <Checkbox
                            key={option.rate_structure_type_id}
                            label={option.rate_structure_type_name}
                            checked={selectedValue === option.rate_structure_type_id}
                            onChange={() => handleChange(option.rate_structure_type_id)}
                            size="medium"
                            name="rateStructure"
                        />
                    ))}
                </div>
            </div>

            {/* Meal Plan Section */}
            {getSelectedRateStructureName() === 2 && (
                <div className={styles.conditionalSection}>
                    <b className={styles.sectionLabel}>
                        Select Meal Plans:
                    </b>
                    <div className={styles.checkboxGroup}>
                        {mealPlanTypes.map((mealPlanType) => (
                            <Checkbox
                                key={mealPlanType.meal_plan_type_id}
                                label={mealPlanType.meal_plan_type_name}
                                checked={selectedMealPlanIds.includes(mealPlanType.meal_plan_type_id)}
                                onChange={(checked) => handleMealPlanToggle(mealPlanType.meal_plan_type_id, checked)}
                                size="medium"
                            />
                        ))}
                    </div>
                    {showValidationErrors && mealPlanError && (
                        <div className={styles.errorMessage}>
                            {mealPlanError}
                        </div>
                    )}
                </div>
            )}

            {/* GIT/FIT Section */}
            {getSelectedRateStructureName() === 3 && (
                <div className={styles.conditionalSection}>
                    <div className={styles.gitFitContainer}>
                        <span>GIT rate applies only to booking &gt;</span>
                        <div className={styles.gitFitInputWrapper}>
                            <InputText
                                label="Rooms"
                                value={gitFitMinRoomCount.toString()}
                                onChange={(e) => {
                                    const rooms = parseInt(e.target.value) || 0;
                                    onGitFitChange?.(rooms, gitFitMinPersonCount);
                                }}
                                type="number"
                                hideLabel={true}
                                noMargin={true}
                                height="36px"
                                sx={{ paddingRight: '50px' }}
                            />
                            <span className={styles.gitFitInputSuffix}>Rooms</span>
                        </div>
                        {/* <span>rooms or</span>
                        <div className={styles.gitFitInputWrapper}>
                            <InputText
                                label="Persons"
                                value={gitFitMinPersonCount.toString()}
                                onChange={(e) => {
                                    const persons = parseInt(e.target.value) || 0;
                                    onGitFitChange?.(gitFitMinRoomCount, persons);
                                }}
                                type="number"
                                hideLabel={true}
                                noMargin={true}
                                height="36px"
                            />
                        </div>
                        <span>persons</span> */}
                    </div>
                    {showValidationErrors && gitFitError && (
                        <div className={styles.errorMessage}>
                            {gitFitError}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RateStrucher;